import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/useLang";
import { getAppStrings, QUESTION_META } from "../i18n/appI18n";

const fmt = (s, vars) => Object.keys(vars || {}).reduce((acc, k) => acc.replace(`{${k}}`, vars[k]), s);
const CAT_COLORS = { values: "text-reveal-rose", emotional: "text-[#d8a48a]", daily: "text-[#e0b79f]", depth: "text-[#c9987b]" };

export default function Onboarding() {
  const { refresh } = useAuth();
  const lang = useLang();
  const s = getAppStrings(lang);
  const ob = s.onboarding;
  const total = QUESTION_META.length;

  const [step, setStep] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const meta = step >= 0 ? QUESTION_META[step] : null;
  const q = meta ? s.questions[meta.id] : null;
  const cat = meta ? ob.categories[meta.category] : null;
  const value = meta ? answers[meta.id] : undefined;
  const canProceed = meta ? (meta.type === "text" ? true : value !== undefined) : true;

  const setAnswer = (v) => setAnswers((a) => ({ ...a, [meta.id]: v }));

  const next = async () => {
    if (step < total - 1) { setStep((x) => x + 1); return; }
    // final submit
    const required = QUESTION_META.filter((m) => m.type === "choice");
    if (required.some((m) => answers[m.id] === undefined)) {
      toast.error(ob.required);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/onboarding", { answers });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Please try again.");
      setSubmitting(false);
    }
  };

  const back = () => setStep((x) => Math.max(-1, x - 1));

  if (step === -1) {
    return (
      <div className="min-h-screen bg-reveal-black text-reveal-cream font-body flex items-center justify-center px-5" data-testid="onboarding-intro">
        <div className="w-full max-w-lg text-center">
          <span className="font-heading text-3xl text-reveal-rose block mb-10">Reveal</span>
          <h1 className="font-heading text-4xl sm:text-5xl text-reveal-cream mb-5 leading-tight">{ob.title}</h1>
          <p className="text-base text-reveal-cream/60 mb-10 leading-relaxed max-w-md mx-auto">{ob.subtitle}</p>
          <button onClick={() => setStep(0)} data-testid="onboarding-start-button"
            className="inline-flex items-center gap-2 bg-reveal-rose text-black font-medium px-10 py-3.5 rounded-full hover:bg-[#c9987b] transition-colors">
            {ob.start} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body px-5 py-10" data-testid="onboarding-page">
      <div className="max-w-lg mx-auto">
        {/* progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs uppercase tracking-widest ${CAT_COLORS[meta.category]}`}>{cat.name}</span>
            <span className="text-xs text-reveal-cream/40" data-testid="onboarding-progress">{fmt(ob.progress, { n: step + 1, total })}</span>
          </div>
          <div className="h-1 rounded-full bg-reveal-rose/15 overflow-hidden">
            <div className="h-full bg-reveal-rose transition-all duration-300" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
          <p className="text-[11px] text-reveal-cream/35 mt-2 italic">{cat.tag}</p>
        </div>

        <h2 className="font-heading text-3xl text-reveal-cream mb-7 leading-snug" data-testid="onboarding-question-text">{q.text}</h2>

        {meta.type === "choice" ? (
          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const selected = value === String(i);
              return (
                <button key={i} onClick={() => setAnswer(String(i))} data-testid={`onboarding-option-${i}`}
                  className={`w-full text-left px-5 py-4 rounded-2xl border transition-colors ${
                    selected
                      ? "bg-reveal-rose/15 border-reveal-rose text-reveal-cream"
                      : "bg-reveal-burgundy/40 border-reveal-rose/20 text-reveal-cream/80 hover:border-reveal-rose/50"
                  }`}>
                  <span className="flex items-center gap-3">
                    <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-reveal-rose bg-reveal-rose" : "border-reveal-rose/40"}`}>
                      {selected && <Check className="w-3 h-3 text-black" />}
                    </span>
                    <span className="text-sm leading-relaxed">{opt}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <textarea rows={4} value={value || ""} onChange={(e) => setAnswer(e.target.value)}
            data-testid="onboarding-text-input" placeholder={ob.textPlaceholder}
            className="w-full bg-black/40 border border-reveal-rose/40 rounded-2xl px-5 py-4 text-reveal-cream placeholder:text-reveal-cream/35 outline-none focus:border-reveal-rose transition-colors resize-none" />
        )}

        <div className="flex items-center justify-between mt-9">
          <button onClick={back} data-testid="onboarding-back-button"
            className="inline-flex items-center gap-2 text-sm text-reveal-cream/60 hover:text-reveal-cream transition-colors">
            <ArrowLeft className="w-4 h-4" /> {ob.back}
          </button>
          <button onClick={next} disabled={!canProceed || submitting} data-testid="onboarding-next-button"
            className="inline-flex items-center gap-2 bg-reveal-rose text-black font-medium px-8 py-3 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-40">
            {submitting ? ob.submitting : step === total - 1 ? ob.finish : ob.next}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
